import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listFolderContents,
  createFolder,
  renameFolder,
  moveFolder,
  deleteFolder,
  moveRecipe,
} from '@/api/dashboard/folders.api';
import useWorkspacesStore from '@/state/workspaces.state';
import { FOLDER_CONTENTS_STALE_TIME_MS } from './queries.consts';
import type { FolderScope } from '@/enums/folder-scope.enum';
import type { ListFolderResponseDto } from '@/types/folder.types';

export const FOLDER_CONTENTS_QUERY_KEY = 'folderContents';

export const useGetFolderContents = (scope: FolderScope) => {
  return useQuery({
    queryKey: [FOLDER_CONTENTS_QUERY_KEY, scope],
    queryFn: () => listFolderContents(scope),
    staleTime: FOLDER_CONTENTS_STALE_TIME_MS,
  });
};

export function useCreateFolder() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (variables: { name: string; scope: FolderScope; parentId?: string | null }) => {
      const workspaceId = useWorkspacesStore.getState().activeWorkspace.workspaceId;
      return createFolder(variables.name, variables.scope, workspaceId, variables.parentId);
    },

    onSuccess: (newFolder, variables) => {
      qc.setQueryData<ListFolderResponseDto>([FOLDER_CONTENTS_QUERY_KEY, variables.scope], (old) => {
        if (!old) return old;
        return { ...old, folders: [...old.folders, newFolder] };
      });
    },
  });
}

export function useRenameFolder() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (variables: { id: string; name: string; scope: FolderScope }) =>
      renameFolder(variables.id, variables.name),

    onMutate: async (variables) => {
      await qc.cancelQueries({ queryKey: [FOLDER_CONTENTS_QUERY_KEY, variables.scope] });

      qc.setQueryData<ListFolderResponseDto>([FOLDER_CONTENTS_QUERY_KEY, variables.scope], (old) => {
        if (!old) return old;
        return {
          ...old,
          folders: old.folders.map((f) => (f.id === variables.id ? { ...f, name: variables.name } : f)),
        };
      });
    },

    onError: (_err, variables) => {
      void qc.invalidateQueries({ queryKey: [FOLDER_CONTENTS_QUERY_KEY, variables.scope] });
    },
  });
}

export function useMoveFolder() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (variables: { id: string; parentId: string | null; scope: FolderScope; sourceScope: FolderScope }) =>
      moveFolder(variables.id, variables.parentId, variables.scope),

    onMutate: async (variables) => {
      await qc.cancelQueries({ queryKey: [FOLDER_CONTENTS_QUERY_KEY, variables.sourceScope] });

      if (variables.sourceScope === variables.scope) {
        qc.setQueryData<ListFolderResponseDto>([FOLDER_CONTENTS_QUERY_KEY, variables.scope], (old) => {
          if (!old) return old;
          return {
            ...old,
            folders: old.folders.map((f) => (f.id === variables.id ? { ...f, parentId: variables.parentId } : f)),
          };
        });
      } else {
        qc.setQueryData<ListFolderResponseDto>([FOLDER_CONTENTS_QUERY_KEY, variables.sourceScope], (old) => {
          if (!old) return old;
          return { ...old, folders: old.folders.filter((f) => f.id !== variables.id) };
        });
      }
    },

    onSuccess: (_data, variables) => {
      if (variables.sourceScope !== variables.scope) {
        void qc.invalidateQueries({ queryKey: [FOLDER_CONTENTS_QUERY_KEY, variables.sourceScope] });
        void qc.invalidateQueries({ queryKey: [FOLDER_CONTENTS_QUERY_KEY, variables.scope] });
      }
    },

    onError: (_err, variables) => {
      void qc.invalidateQueries({ queryKey: [FOLDER_CONTENTS_QUERY_KEY, variables.sourceScope] });
      void qc.invalidateQueries({ queryKey: [FOLDER_CONTENTS_QUERY_KEY, variables.scope] });
    },
  });
}

export function useDeleteFolder() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (variables: { id: string; scope: FolderScope }) => deleteFolder(variables.id),

    onSuccess: (_data, variables) => {
      qc.setQueryData<ListFolderResponseDto>([FOLDER_CONTENTS_QUERY_KEY, variables.scope], (old) => {
        if (!old) return old;
        return {
          ...old,
          folders: old.folders.filter((f) => f.id !== variables.id),
        };
      });
    },

    onError: (_err, variables) => {
      void qc.invalidateQueries({ queryKey: [FOLDER_CONTENTS_QUERY_KEY, variables.scope] });
    },
  });
}

export function useMoveRecipe() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (variables: { id: string; folderId: string | null; scope: FolderScope; sourceScope: FolderScope }) =>
      moveRecipe(variables.id, variables.folderId, variables.scope),

    onMutate: async (variables) => {
      await qc.cancelQueries({ queryKey: [FOLDER_CONTENTS_QUERY_KEY, variables.sourceScope] });

      if (variables.sourceScope === variables.scope) {
        qc.setQueryData<ListFolderResponseDto>([FOLDER_CONTENTS_QUERY_KEY, variables.scope], (old) => {
          if (!old) return old;
          return {
            ...old,
            recipes: old.recipes.map((r) => (r.id === variables.id ? { ...r, folderId: variables.folderId } : r)),
          };
        });
      } else {
        qc.setQueryData<ListFolderResponseDto>([FOLDER_CONTENTS_QUERY_KEY, variables.sourceScope], (old) => {
          if (!old) return old;
          return { ...old, recipes: old.recipes.filter((r) => r.id !== variables.id) };
        });
      }
    },

    onSuccess: (_data, variables) => {
      if (variables.sourceScope !== variables.scope) {
        void qc.invalidateQueries({ queryKey: [FOLDER_CONTENTS_QUERY_KEY, variables.sourceScope] });
        void qc.invalidateQueries({ queryKey: [FOLDER_CONTENTS_QUERY_KEY, variables.scope] });
      }
    },

    onError: (_err, variables) => {
      void qc.invalidateQueries({ queryKey: [FOLDER_CONTENTS_QUERY_KEY, variables.sourceScope] });
      void qc.invalidateQueries({ queryKey: [FOLDER_CONTENTS_QUERY_KEY, variables.scope] });
    },
  });
}
